import { useState } from 'react'
import { ShieldCheck } from 'lucide-react'
import { Button, Input, Modal } from '@/components/ui'
import type { PriceReviewLine } from '../api/reviewTypes'
import { formatMoney } from '../utils/letter-lines'
import { summarize } from '../utils/review-lines'

const SHOWN = 12

export interface ApproveReviewModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  customer: string
  symbol: string
  lines: PriceReviewLine[]
  /** Effective date already on the review, if any. */
  initialDate: string | null
  pending: boolean
  error: string | null
  onConfirm: (effectiveDate: string) => void
}

/**
 * Approving commits to Odoo, so it gets a confirmation that says exactly what
 * will be written, on what date, and what will not happen.
 */
export function ApproveReviewModal({
  open,
  onOpenChange,
  customer,
  symbol,
  lines,
  initialDate,
  pending,
  error,
  onConfirm,
}: ApproveReviewModalProps) {
  const [date, setDate] = useState(initialDate ?? '')
  const summary = summarize(lines)
  const acting = lines.filter((l) => l.action !== 'keep')
  const movesParts = acting.some((l) => l.action === 'change' && l.source === 'pricelist' && !!l.customer_part_number)
  const nothingToDo = acting.length === 0
  const blocked = !date || summary.missingPrice > 0 || nothingToDo

  return (
    <Modal
      surface="form"
      open={open}
      onOpenChange={onOpenChange}
      tone="warning"
      icon={ShieldCheck}
      title={`Approve and push prices for ${customer}?`}
      description="This writes to the customer's pricelist in Odoo."
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={() => onConfirm(date)} disabled={blocked || pending}>
            {pending ? 'Pushing to Odoo…' : 'Approve and push'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-role-section-label">Prices take effect on</span>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>

        <ul className="m-0 flex list-disc flex-col gap-1 pl-5 text-sm text-secondary">
          <li>
            {summary.changes} price {summary.changes === 1 ? 'change' : 'changes'}
            {summary.discontinues ? ` and ${summary.discontinues} ${summary.discontinues === 1 ? 'product' : 'products'} removed` : ''}.
            {` ${summary.kept} ${summary.kept === 1 ? 'line stays' : 'lines stay'} as they are.`}
          </li>
          <li>Each new price starts at midnight Central on that date; the old price ends a second before, so Odoo switches by itself.</li>
          {movesParts ? (
            <li>
              Customer part numbers move to the new price rows: Odoo allows each part number once per pricelist, so the old
              rows keep their price history without the number.
            </li>
          ) : null}
          <li>Nothing is emailed. Create the customer letter from the review afterwards.</li>
        </ul>

        {summary.missingPrice > 0 ? (
          <p className="m-0 text-sm text-danger">
            {summary.missingPrice}{' '}
            {summary.missingPrice === 1 ? 'line is marked Change but has no new price.' : 'lines are marked Change but have no new price.'}
          </p>
        ) : null}
        {summary.flaggedActing > 0 ? (
          <p className="m-0 text-sm text-warning-text">
            {summary.flaggedActing} of the lines being pushed {summary.flaggedActing === 1 ? 'is' : 'are'} still flagged for discussion.
          </p>
        ) : null}
        {nothingToDo ? <p className="m-0 text-sm text-danger">No line has a changed price or a discontinue.</p> : null}

        {acting.length > 0 ? (
          <div className="max-h-56 overflow-y-auto rounded-sm border border-border-subtle">
            <table className="w-full text-sm">
              <tbody>
                {acting.slice(0, SHOWN).map((l) => (
                  <tr key={l.id} className="border-b border-border-subtle last:border-b-0">
                    <td className="px-2 py-1 text-primary">
                      {l.product_name}
                      {l.min_quantity ? <span className="text-role-metadata"> · min {l.min_quantity}</span> : null}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums text-secondary">
                      {l.action === 'discontinue'
                        ? 'Removed from pricelist'
                        : `${l.old_price === null ? 'new' : formatMoney(symbol, l.old_price)} → ${formatMoney(symbol, l.new_price)}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {acting.length > SHOWN ? (
              <p className="m-0 px-2 py-1 text-role-metadata">+ {acting.length - SHOWN} more</p>
            ) : null}
          </div>
        ) : null}

        {error ? <p className="m-0 text-xs text-danger">{error}</p> : null}
      </div>
    </Modal>
  )
}
